import { TSummarySubmissionResult } from 'widgets/checkout/SummaryStepMixin';
import SmsSubscriptionMixinClassCreator from 'core/global/SmsSubscriptionMixin';
import { TAjaxForm } from 'widgets/forms/AjaxForm';
import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';

/**
 * @param AjaxForm Base widget for extending
 * @returns Address Form widget
 */
function SummaryFormClassCreator<TBase extends TAjaxForm>(Base: TBase) {
    const AjaxFormWithSms = SmsSubscriptionMixinClassCreator(Base);

    class CoreSummaryForm extends AjaxFormWithSms {
        skipPrivacyValidation = false;

        urlPlaceOrder = '';

        expressCheckoutType: string | undefined;

        prefs() {
            return {
                gtmSubscriptionSource: '',
                ...super.prefs(),
                privacyFieldName: 'dwfrm_summary_agreeToPrivacy',
                addToEmailListId: 'addToEmailList'
            };
        }

        /**
         * @description Get Form fields
         * @returns Object contains form fields
         */
        getFormFields(): { [key: string]: string | undefined } {
            const fields = super.getFormFields();

            if (this.expressCheckoutType) {
                fields.expressCheckoutType = this.expressCheckoutType;
            }

            return fields;
        }

        /**
         * @description Submit form handler
         */
        submit(): Promise<TSummarySubmissionResult> | void {
            if (this.skipPrivacyValidation) {
                this.eachField(field => {
                    if (field.getName() === this.prefs().privacyFieldName) {
                        field.setPref('skipValidation', this.skipPrivacyValidation);
                        field.ref('field').prop('required', false);
                    }
                });
            }

            const isApplePayPlaceOrderButton = document.querySelector('div[value="applepay_placeorder"]');

            if (this.isValid()) {
                if (isApplePayPlaceOrderButton) {
                    return Promise.resolve({} as TSummarySubmissionResult);
                } else {
                    return this.handleSubmit();
                }
            }

            this.validate();

            return Promise.reject(new Error('Server response is not valid'));
        }

        setSkipPrivacyValidation(skipPrivacyValidation) {
            this.skipPrivacyValidation = skipPrivacyValidation;
        }

        /**
         * @description Handles server response
         * @emits AjaxForm#submit
         * @param data Server JSON response once form submitted
         */
        onSubmitted(data: TServerResponse): void {
            if (data?.success) {
                this.handleGTMUpdates(data);
            }
        }

        /**
         * @description Handles GTM tracking updates
         * @emits newsletter.subscribed
         */
        handleGTMUpdates(data: TServerResponse) {
            this.getById(this.prefs().addToEmailListId, (addToEmailListCheckbox: TInputCheckboxInstance) => {
                const isChecked = Boolean(addToEmailListCheckbox.getValue());

                if (isChecked) {
                    this.eventBus().emit('newsletter.subscribed', this, data);
                }
            });
        }

        getFormUrl(): string {
            return this.urlPlaceOrder || this.ref('self').attr('action').toString();
        }
    }

    return CoreSummaryForm;
}

export type TSummaryForm = ReturnType<typeof SummaryFormClassCreator>;

export type TSummaryFormInstance = InstanceType<TSummaryForm>;

export default SummaryFormClassCreator;
